import { useCallback, useState } from 'react';
import { useAlert } from 'shared/components/alerts';
import { flexPoolAlerts as alerts } from 'shared/components/alerts/alert-messages';
import { useQueryClient } from 'react-query';
import { invariant } from 'shared/utils/utils';
import { QUERY_KEYS } from 'api/query-keys';
import {
  FlexWorkerFlexPoolsData,
  useOgpUpdateFlexWorkerFlexPools,
} from './hooks/ogp/use-ogp-update-flex-worker-flex-pools';
import { useHqUpdateFlexWorkerFlexPools } from './hooks/hq/use-hq-update-flex-worker-flex-pools';
import { useOgpGetFlexWorkerFlexPools } from './hooks/ogp/use-ogp-flex-worker-flex-pools';
import { useHqGetFlexWorkerFlexPools } from './hooks/hq/use-hq-flex-worker-flex-pools';
import { FlexWorkerFlexPoolsFormData } from './choose-flex-worker-flex-pool-dialog.types';
import { ChooseFlexWorkerFlexPoolDialog } from './form/choose-flex-worker-flex-pool-dialog';

type MutationFn = typeof useOgpUpdateFlexWorkerFlexPools | typeof useHqUpdateFlexWorkerFlexPools;
type QueryFn = typeof useOgpGetFlexWorkerFlexPools | typeof useHqGetFlexWorkerFlexPools;

export function useChooseFlexWorkerFlexPoolsDialog(
  mutation: MutationFn,
  queryFn: QueryFn,
  companyId?: string
) {
  const queryClient = useQueryClient();
  const [shiftId, setShiftId] = useState<string>('');
  const [flexWorkerId, setFlexWorkerId] = useState<string>('');
  const [flexWorkerFullName, setFlexWorkerFullName] = useState<string>('');
  const [isOpen, setIsOpen] = useState(false);
  const { alertSuccess, alertError } = useAlert();
  const updateMutation = mutation();
  const query = queryFn(flexWorkerId, shiftId);

  const open = useCallback((shiftId: string, flexworkerId: string, flexWorkerFullName: string) => {
    setIsOpen(true);
    setShiftId(shiftId);
    setFlexWorkerId(flexworkerId);
    setFlexWorkerFullName(flexWorkerFullName);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onSubmit = (formData: FlexWorkerFlexPoolsFormData) => {
    invariant(query.data);
    const selectedOptions: Record<string, boolean> = {};
    formData.flexworkerFlexPoolsOptions.forEach((flexWorkerFlexPool) => {
      selectedOptions[flexWorkerFlexPool.flexPoolId] = true;
    });

    const requestData: FlexWorkerFlexPoolsData = {
      flexWorkerId,
      flexPoolsToRemove: [],
      flexPoolsToAdd: [],
      companyId,
    };

    query.data.forEach((flexWorkerFlexPools) => {
      if (selectedOptions[flexWorkerFlexPools.flexPoolId] && !flexWorkerFlexPools.isMember) {
        requestData.flexPoolsToAdd.push(flexWorkerFlexPools.flexPoolId);
      } else if (!selectedOptions[flexWorkerFlexPools.flexPoolId] && flexWorkerFlexPools.isMember) {
        requestData.flexPoolsToRemove.push(flexWorkerFlexPools.flexPoolId);
      }
    });

    updateMutation.mutate(requestData, {
      onSuccess: () => {
        close();
        alertSuccess(alerts.success.updated);
        queryClient.invalidateQueries(QUERY_KEYS.shifts());
        queryClient.invalidateQueries(QUERY_KEYS.worker(flexWorkerId));
      },
      onError: (e) => {
        close();
        alertError(e);
      },
    });
  };

  const Element = (
    <ChooseFlexWorkerFlexPoolDialog
      flexWorkerFullName={flexWorkerFullName}
      query={query}
      isOpen={isOpen}
      onClose={close}
      onSubmit={onSubmit}
    />
  );

  return { open, close: close, Element };
}
